import { useNavigate } from 'react-router-dom';
import { Stack, Group, Text, Box } from '@repletoryreach/sdk-ui';
import { StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import type { admin } from '@repletoryreach/sdk-api';
import { formatDate, formatInteger } from '../../../lib/format';
import { TenantAvatar } from './TenantAvatar';
import { useT } from '../../../i18n/I18nProvider';

type Tenant = NonNullable<Awaited<ReturnType<typeof admin.listTenants>>['data']>[number];

/**
 * Vertical list view: each row is a horizontal card with key metadata,
 * denser than the grid but more breathable than the table. Useful when
 * users want to scan tenants by name without lots of columns.
 */
export function TenantsList({ records }: { records: Tenant[] }) {
  const navigate = useNavigate();
  const t = useT();
  return (
    <Stack gap="sm">
      {records.map((tenant) => (
        <button
          key={tenant.id}
          type="button"
          onClick={() => navigate(`/tenants/${tenant.id}`)}
          className="rr-list-row"
        >
          <Group gap="md" align="center" wrap="nowrap" style={{ width: '100%' }}>
            <span style={{ flexShrink: 0, display: 'inline-flex' }}><TenantAvatar name={tenant.name} size="md" /></span>
            <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
              <Group gap="xs" align="baseline" wrap="nowrap">
                <Text role="subtitle" fw={600} truncate>{tenant.name}</Text>
                <Text role="caption" c="var(--rr-color-text-muted)"><code>{tenant.slug}</code></Text>
              </Group>
              <Text role="caption" c="var(--rr-color-text-muted)">
                {tenant.country_iso} · {tenant.timezone} · {t('tenants.createdAt', { date: formatDate(tenant.created_at) })}
              </Text>
            </Stack>
            <Box style={{ minWidth: 80, textAlign: 'right' }}>
              <Text role="bodySmall" fw={700}>{formatInteger(tenant.user_count)}</Text>
              <Text role="caption" c="var(--rr-color-text-muted)">{t('tenants.list.users')}</Text>
            </Box>
            <StatusBadge size="sm" status={tenant.is_active ? 'healthy' : 'inactive'} style={{ flexShrink: 0 }}>
              {tenant.is_active ? t('status.active') : t('status.inactive')}
            </StatusBadge>
          </Group>
        </button>
      ))}
    </Stack>
  );
}
